/**
 * Écran « Monitoring financier » (API v2 `/finance`).
 *
 * - Montants multi-devises affichés PAR DEVISE, jamais additionnés
 *   (MON-11-F) ; aucune devise par défaut.
 * - Métriques de stock (en attente, abonnements…) évaluées au `as_of` et
 *   libellées « au JJ/MM/AAAA » (D-01) ; une période future montre donc les
 *   expirations à venir.
 * - Seuil « expire bientôt » lu dans la réponse (`threshold_days`), jamais
 *   codé en dur.
 * - Qualité des données (admin) : un écart de rapprochement déclenche un
 *   bandeau d'alerte.
 */
import React, { useState } from 'react';
import { Alert, Card, Col, Row, Select, Space, Typography } from 'antd';
import {
    ClockCircleOutlined,
    ExclamationCircleOutlined,
    FileTextOutlined,
    GiftOutlined,
    HourglassOutlined,
    SafetyCertificateOutlined,
    StopOutlined,
    WalletOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import AppLayout from '../../components/layout/AppLayout';
import {
    INVOICE_NATURES,
    INVOICE_STATUSES,
    PAYMENT_NATURES,
    PAYMENT_PROVIDERS,
    SUBSCRIPTION_STATES,
    type EmptyReason,
    type ExportDataset,
    type FinanceParams,
    type FinanceResponse,
    type Table,
} from '../../api/monitoring/v2';
import { INK, cardStyle } from './monitoringTheme';
import MonitoringFilterBar from './shared/MonitoringFilterBar';
import StatTile from './shared/StatTile';
import CompactValue from './shared/CompactValue';
import { MoneyMultiText } from './shared/MoneyText';
import RatioText from './shared/RatioText';
import EmptyState, { hasEmptyReason } from './shared/EmptyState';
import TimeSeriesChart from './shared/TimeSeriesChart';
import DataTable from './shared/DataTable';
import ExportButton from './shared/ExportButton';
import QueryStatusView from './shared/QueryStatusView';
import MetaBanner from './shared/MetaBanner';
import { useMonitoringQuery } from './shared/useMonitoringQuery';
import { useMonitoringApiRef, useScopePreselect, useScopes } from './shared/useScopes';
import { filtersReady, toCommonParams, useMonitoringFilters } from './shared/useMonitoringFilters';
import { useFormat } from './shared/format';
import { useServerTable } from './shared/useServerTable';
import { useEnumLabel } from './shared/i18nCatalog';

const { Text, Title } = Typography;

const TABS = ['finance'] as const;

type ServerTable = ReturnType<typeof useServerTable>;

/** Filtre propre d'un tableau (§10.3) : nom du paramètre, énumération, codes. */
type TableFilterControl = { name: string; enumName: string; codes: readonly string[]; label: string };

const FinanceTable: React.FC<{
    title: string;
    table: Table;
    dataset: ExportDataset;
    common: ReturnType<typeof toCommonParams>;
    server: ServerTable;
    filterControls: TableFilterControl[];
    reasons: EmptyReason[];
    only: readonly EmptyReason[];
    loading: boolean;
}> = ({ title, table, dataset, common, server, filterControls, reasons, only, loading }) => {
    const enumLabel = useEnumLabel();
    return (
        <Card
            style={cardStyle}
            title={
                <Space size={12} wrap style={{ width: '100%', justifyContent: 'space-between' }}>
                    <Title level={5} style={{ margin: 0 }}>
                        {title}
                    </Title>
                    <Space size={8} wrap>
                        {filterControls.map((control) => (
                            <Select
                                key={control.name}
                                allowClear
                                size="small"
                                style={{ minWidth: 170 }}
                                aria-label={control.label}
                                placeholder={control.label}
                                value={server.filterValues[control.name]}
                                onChange={(v?: string) => server.setFilter(control.name, v)}
                                options={control.codes.map((code) => ({ value: code, label: enumLabel(control.enumName, code) }))}
                            />
                        ))}
                    </Space>
                </Space>
            }
            extra={<ExportButton dataset={dataset} params={{ ...common, table: server.filters }} />}
        >
            <Space direction="vertical" size={12} style={{ width: '100%' }}>
                <EmptyState reasons={reasons} only={only} />
                <DataTable
                    table={table}
                    rowKey={(row, i) => (row.id !== undefined && row.id !== null ? String(row.id) : `row-${i}`)}
                    loading={loading}
                    server={{ state: server.state, onChange: server.setState }}
                />
            </Space>
        </Card>
    );
};

const MonitoringFinance: React.FC = () => {
    const { t } = useTranslation();
    const f = useFormat();
    const apiRef = useMonitoringApiRef();
    const [refreshToken, setRefreshToken] = useState(0);
    // `/scopes` d'abord : il fournit `today`, d'où part la période par défaut.
    const scopes = useScopes(refreshToken);
    const { filters, preset, setPeriod, setScope } = useMonitoringFilters(TABS, 'finance', scopes.data?.today ?? null);
    useScopePreselect(scopes.data, filters, setScope);

    const common = toCommonParams(filters);
    const group = JSON.stringify(['finance', common]);
    // Les trois tableaux sont paginés et filtrés par le serveur, chacun sous sa
    // clé (§10.2-3) ; changer de page ne recharge pas l'écran (même groupe).
    const invoicesTable = useServerTable(group);
    const paymentsTable = useServerTable(group);
    const subscriptionsTable = useServerTable(group);
    const params: FinanceParams = {
        ...common,
        invoices: invoicesTable.query,
        payments: paymentsTable.query,
        subscriptions: subscriptionsTable.query,
    };
    const ready = scopes.status === 'success' && filtersReady(filters);
    const query = useMonitoringQuery<FinanceResponse>(
        JSON.stringify(['finance', params]),
        (signal) => apiRef.current.getFinance(params, signal),
        { refreshToken, enabled: ready, groupKey: group }
    );
    const data = query.data;
    const tiles = data?.tiles;
    const asOf = data?.meta.period.as_of;
    const title = t('monitoring.finance.title');

    if (scopes.status === 'role_required' || query.status === 'role_required') {
        return (
            <AppLayout title={title}>
                <Alert type="error" showIcon data-testid="role-required" message={t('monitoring.errors.roleRequired')} />
            </AppLayout>
        );
    }

    const count = (n: number) => <CompactValue value={n} />;
    const refreshing = query.status === 'refreshing';

    return (
        <AppLayout title={title}>
            <Space direction="vertical" size={16} style={{ width: '100%' }}>
                <MonitoringFilterBar
                    scopes={scopes.data}
                    filters={filters}
                    preset={preset}
                    onScopeChange={setScope}
                    onPeriodChange={setPeriod}
                    onRefresh={() => setRefreshToken((n) => n + 1)}
                    refreshing={refreshing}
                />
                {scopes.status === 'error' && <Alert type="error" showIcon message={t('monitoring.errors.scopes')} />}

                <QueryStatusView query={query} tiles={12} />

                {data && tiles && (
                    <>
                        <MetaBanner meta={data.meta} />
                        {tiles.data_quality && tiles.data_quality.reconciliation_gaps > 0 && (
                            <Alert
                                type="warning"
                                showIcon
                                data-testid="reconciliation-alert"
                                message={t('monitoring.finance.reconciliationAlert', {
                                    n: f.compact(tiles.data_quality.reconciliation_gaps).text,
                                })}
                                description={t('metric.FIN-DQ.help')}
                            />
                        )}
                        <EmptyState reasons={data.empty_reasons} only={['future_period', 'no_students']} />

                        <Text strong style={{ color: INK.secondary }}>
                            {t('monitoring.finance.flowSection')}
                        </Text>
                        <Row gutter={[12, 12]}>
                            <Col xs={12} sm={8} lg={6} xl={4}>
                                <StatTile metricId="FIN-01" icon={<WalletOutlined />} value={<MoneyMultiText values={tiles.revenue} />} />
                            </Col>
                            <Col xs={12} sm={8} lg={6} xl={4}>
                                <StatTile metricId="FIN-02" value={<MoneyMultiText values={tiles.refunds} />} />
                            </Col>
                            <Col xs={12} sm={8} lg={6} xl={4}>
                                <StatTile metricId="FIN-03" icon={<FileTextOutlined />} value={count(tiles.invoices_issued)} />
                            </Col>
                            <Col xs={12} sm={8} lg={6} xl={4}>
                                <StatTile metricId="FIN-GRANTS" icon={<GiftOutlined />} value={count(tiles.offered_grants)} />
                            </Col>
                            <Col xs={12} sm={8} lg={6} xl={4}>
                                <StatTile metricId="FIN-11" value={<MoneyMultiText values={tiles.arppu} />} />
                            </Col>
                            <Col xs={12} sm={8} lg={6} xl={4}>
                                <StatTile metricId="FIN-13" icon={<StopOutlined />} value={count(tiles.abandoned_attempts)} />
                            </Col>
                        </Row>

                        <Text strong style={{ color: INK.secondary }}>
                            {t('monitoring.finance.stockSection', { date: f.date(asOf) })}
                        </Text>
                        <Row gutter={[12, 12]}>
                            <Col xs={24} sm={12} lg={8} xl={6}>
                                <StatTile
                                    metricId="FIN-04"
                                    icon={<HourglassOutlined />}
                                    asOf={asOf}
                                    value={
                                        <Space direction="vertical" size={2} style={{ fontSize: 14, fontWeight: 500 }}>
                                            <span>
                                                <Text style={{ fontSize: 12, color: INK.secondary }}>
                                                    {t('monitoring.finance.pendingFamilies')} :{' '}
                                                </Text>
                                                <MoneyMultiText values={tiles.pending.families} inline />
                                            </span>
                                            <span>
                                                <Text style={{ fontSize: 12, color: INK.secondary }}>
                                                    {t('monitoring.finance.pendingSchools')} :{' '}
                                                </Text>
                                                <MoneyMultiText values={tiles.pending.schools} inline />
                                            </span>
                                        </Space>
                                    }
                                />
                            </Col>
                            <Col xs={12} sm={8} lg={6} xl={3}>
                                <StatTile metricId="FIN-05" icon={<SafetyCertificateOutlined />} asOf={asOf} value={count(tiles.subscriptions_active)} />
                            </Col>
                            <Col xs={12} sm={8} lg={6} xl={3}>
                                <StatTile
                                    metricId="FIN-06"
                                    icon={<ClockCircleOutlined />}
                                    asOf={asOf}
                                    labelValues={{ days: tiles.expiring_soon.threshold_days }}
                                    value={count(tiles.expiring_soon.value)}
                                />
                            </Col>
                            <Col xs={12} sm={8} lg={6} xl={3}>
                                <StatTile metricId="FIN-07" icon={<ExclamationCircleOutlined />} asOf={asOf} value={count(tiles.expired)} />
                            </Col>
                            <Col xs={12} sm={8} lg={6} xl={3}>
                                <StatTile metricId="FIN-08" asOf={asOf} value={count(tiles.awaiting_first_payment)} />
                            </Col>
                            <Col xs={12} sm={8} lg={6} xl={3}>
                                <StatTile metricId="FIN-09" asOf={asOf} value={count(tiles.offered_access)} />
                            </Col>
                            <Col xs={12} sm={8} lg={6} xl={3}>
                                <StatTile metricId="FIN-10" asOf={asOf} value={<RatioText ratio={tiles.coverage} showFraction />} />
                            </Col>
                        </Row>

                        {!hasEmptyReason(data.empty_reasons, ['future_period', 'no_payments']) && (
                            <TimeSeriesChart
                                title={t('metric.FIN-12.label')}
                                subtitle={t('metric.FIN-12.help')}
                                series={data.series.filter((s) => s.metric === 'FIN-12')}
                                formatValue={(v, currency) => {
                                    if (!currency) {
                                        // eslint-disable-next-line no-console
                                        console.warn('[monitoring] série monétaire sans devise');
                                        return String(v);
                                    }
                                    return f.compact(String(v), currency).text;
                                }}
                            />
                        )}

                        <FinanceTable
                            title={t('monitoring.finance.subscriptions')}
                            table={data.subscriptions}
                            dataset="finance_subscriptions"
                            common={common}
                            server={subscriptionsTable}
                            filterControls={[{ name: 'state', enumName: 'subscription_state', codes: SUBSCRIPTION_STATES, label: t('monitoring.col.state') }]}
                            reasons={data.empty_reasons}
                            only={['no_students']}
                            loading={refreshing}
                        />
                        <FinanceTable
                            title={t('monitoring.finance.invoices')}
                            table={data.invoices}
                            dataset="finance_invoices"
                            common={common}
                            server={invoicesTable}
                            filterControls={[
                                { name: 'status', enumName: 'invoice_status', codes: INVOICE_STATUSES, label: t('monitoring.col.status') },
                                { name: 'nature', enumName: 'invoice_nature', codes: INVOICE_NATURES, label: t('monitoring.col.nature') },
                            ]}
                            reasons={data.empty_reasons}
                            only={['no_invoices']}
                            loading={refreshing}
                        />
                        <FinanceTable
                            title={t('monitoring.finance.payments')}
                            table={data.payments}
                            dataset="finance_payments"
                            common={common}
                            server={paymentsTable}
                            filterControls={[
                                { name: 'nature', enumName: 'payment_nature', codes: PAYMENT_NATURES, label: t('monitoring.col.nature') },
                                { name: 'provider', enumName: 'payment_provider', codes: PAYMENT_PROVIDERS, label: t('monitoring.col.provider') },
                            ]}
                            reasons={data.empty_reasons}
                            only={['no_payments']}
                            loading={refreshing}
                        />
                    </>
                )}
            </Space>
        </AppLayout>
    );
};

export default MonitoringFinance;
